import { featureAccessPolicySchema, additionalAccountRolesSchema } from '@kop/contracts';
import type { SystemManagementRepository, SystemManagementSnapshot } from './model';

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('시스템 관리 응답을 확인하지 못했습니다.');
  return value as Record<string, unknown>;
}

export function createSystemManagementRepository(): SystemManagementRepository {
  const getPort = async () => {
    const { createFeatureAccessManagementPort } = await import('@kop/operations-firebase-adapter');
    return createFeatureAccessManagementPort({
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
    });
  };
  return {
    async load() {
      const data = object(await (await getPort()).load());
      if (!['legacy', 'shadow', 'enforced'].includes(String(data.mode)) ||
          !Number.isSafeInteger(data.version) || Number(data.version) < 0)
        throw new Error('시스템 관리 설정을 확인하지 못했습니다.');
      const publication = data.canPublish === undefined ? {} : (() => {
        if (typeof data.canPublish !== 'boolean' || !Number.isSafeInteger(data.activeVersion) ||
            Number(data.activeVersion) < 0 || !Number.isSafeInteger(data.headGeneration) || Number(data.headGeneration) < 0)
          throw new Error('권한 설정 적용 상태를 확인하지 못했습니다.');
        return { canPublish: data.canPublish, activeVersion: Number(data.activeVersion), headGeneration: Number(data.headGeneration) };
      })();
      return { policy: data.policy == null ? null : featureAccessPolicySchema.parse(data.policy),
        mode: data.mode as SystemManagementSnapshot['mode'], version: Number(data.version), ...publication };
    },
    async saveDraft(input) {
      const data = object(await (await getPort()).saveDraft(input));
      const policy = featureAccessPolicySchema.parse(data.policy);
      if (data.version !== input.expectedVersion + 1 || policy.version !== data.version ||
          JSON.stringify(policy) !== JSON.stringify(featureAccessPolicySchema.parse(input.policy)))
        throw new Error('설정 저장 결과를 확인하지 못했습니다. 다시 조회해 주세요.');
      const publication = data.canPublish === undefined ? {
        canPublish: false,
        activeVersion: 0,
        headGeneration: 0,
      } : (() => {
        if (typeof data.canPublish !== 'boolean' || !Number.isSafeInteger(data.activeVersion) ||
            Number(data.activeVersion) < 0 || !Number.isSafeInteger(data.headGeneration) || Number(data.headGeneration) < 0)
          throw new Error('권한 설정 저장 결과를 확인하지 못했습니다.');
        return {
          canPublish: data.canPublish,
          activeVersion: Number(data.activeVersion),
          headGeneration: Number(data.headGeneration),
        };
      })();
      return { policy, version: Number(data.version), ...publication };
    },
    async publish(input) {
      const data = object(await (await getPort()).publish(input));
      if (data.policyVersion !== input.policyVersion || data.headGeneration !== input.expectedHeadGeneration + 1)
        throw new Error('권한 설정 적용 결과를 확인하지 못했습니다. 같은 요청으로 다시 확인해 주세요.');
      return { policyVersion: Number(data.policyVersion), headGeneration: Number(data.headGeneration) };
    },
    async loadAccountRoles() {
      const data = object(await (await getPort()).loadAccountRoles());
      if (typeof data.canManage !== 'boolean' || !Array.isArray(data.accounts)) throw new Error('계정 권한 목록을 확인하지 못했습니다.');
      const accounts = data.accounts.map(value => {
        const account = object(value);
        if (typeof account.identityId !== 'string' || typeof account.displayName !== 'string' ||
            typeof account.email !== 'string' || typeof account.developer !== 'boolean' ||
            !Number.isSafeInteger(account.version) || Number(account.version) < 0)
          throw new Error('계정 권한 정보를 확인하지 못했습니다.');
        return { identityId: account.identityId, displayName: account.displayName, email: account.email,
          developer: account.developer, version: Number(account.version) };
      });
      return { canManage: data.canManage, accounts };
    },
    async saveAccountRoles(input) {
      const data = object(await (await getPort()).saveAccountRoles(input));
      const additionalRoles = additionalAccountRolesSchema.parse(data.additionalRoles);
      if (data.targetUid !== input.targetUid || data.version !== input.expectedVersion + 1 ||
          JSON.stringify(additionalRoles) !== JSON.stringify(input.additionalRoles))
        throw new Error('계정 권한 변경 결과를 확인하지 못했습니다. 같은 요청으로 다시 확인해 주세요.');
      return { targetUid: data.targetUid as string, version: Number(data.version), additionalRoles };
    },
  };
}
